import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Coins,
  CalendarClock,
  Eye,
  Gem,
  Lock,
  Loader2,
  Sparkles,
  ArrowRight,
  Check,
} from "lucide-react";
import { castScrying, requestOracleSession, fetchOracleSessions } from "@/lib/api";
import CyclicalPlanner from "@/components/CyclicalPlanner";

const ease = [0.22, 1, 0.36, 1];

const TIER_RANK = { "The Novitiate": 0, "The Adept": 1, "The Matriarch": 2 };
const TIERS = ["The Novitiate", "The Adept", "The Matriarch"];

/* ------------------------ Governance Scrying Kit ----------------------- */
const ScryingChamber = () => {
  const [focus, setFocus] = useState("");
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState(false);

  const cast = async () => {
    setBusy(true);
    try {
      setReading(await castScrying(focus.trim()));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The scrying glass is clouded.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5">
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          placeholder="Name the venture or allocation to read…"
          className="flex-1 rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid="scrying-focus-input"
        />
        <button
          onClick={cast}
          disabled={busy}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#C5A059] px-6 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-50"
          data-testid="scrying-cast-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          {busy ? "Scrying…" : "Cast the scrying"}
        </button>
      </div>
      <AnimatePresence>
        {reading && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mt-5 space-y-3"
            data-testid="scrying-result"
          >
            {[
              ["Pattern", reading.pattern],
              ["Opportunity", reading.opportunity],
              ["Risk to guard", reading.risk],
            ].map(([label, body]) => (
              <div key={label} className="rounded-xl border border-[#C5A059]/12 bg-black/30 px-4 py-3">
                <p className="overline text-[0.5rem]">{label}</p>
                <p className="mt-1 text-sm font-light leading-relaxed text-white/65">{body}</p>
              </div>
            ))}
            <div className="rounded-xl border border-[#5a4a1f]/40 bg-[#C5A059]/5 px-4 py-3">
              <p className="overline text-[0.5rem]">Archetype of the cycle</p>
              <p className="mt-1 text-sm font-light italic text-[#e8d3a6]">{reading.archetype}</p>
            </div>
            <p className="pt-1 font-serif text-lg font-light italic leading-relaxed text-white/80">
              “{reading.counsel}”
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ----------------------- Private Oracle Sessions ----------------------- */
const OracleSessionChamber = () => {
  const [form, setForm] = useState({ topic: "", structure: "", preferred_time: "" });
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchOracleSessions().then(setSessions).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.topic.trim().length < 2) return toast.error("Name the matter to be refined.");
    setBusy(true);
    try {
      const s = await requestOracleSession({
        topic: form.topic.trim(),
        structure: form.structure.trim(),
        preferred_time: form.preferred_time.trim(),
      });
      setSessions((prev) => [s, ...prev]);
      setForm({ topic: "", structure: "", preferred_time: "" });
      toast.success("Your private session is requested. The Oracle will answer.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The request could not be sealed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5">
      <form onSubmit={submit} className="space-y-3" data-testid="oracle-session-form">
        <input
          value={form.topic}
          onChange={set("topic")}
          placeholder="Matter to refine (e.g. multi-entity restructuring)"
          className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid="oracle-session-topic"
        />
        <textarea
          value={form.structure}
          onChange={set("structure")}
          rows={2}
          placeholder="The structure at hand (entities, jurisdictions, wealth transfer…)"
          className="w-full resize-none rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid="oracle-session-structure"
        />
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            value={form.preferred_time}
            onChange={set("preferred_time")}
            placeholder="Preferred lunar window / timing"
            className="flex-1 rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
            data-testid="oracle-session-time"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#C5A059] bg-[#C5A059] px-6 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="oracle-session-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Sealing…" : "Request session"}
          </button>
        </div>
      </form>
      {sessions.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="oracle-session-list">
          <p className="overline text-[0.5rem]">Your sessions</p>
          {sessions.map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-[#C5A059]/12 bg-black/30 px-4 py-3"
              data-testid={`oracle-session-${s.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-light text-white/80">{s.topic}</p>
                <span className="shrink-0 rounded-full border border-[#C5A059]/25 px-3 py-1 font-mono text-[0.55rem] uppercase tracking-[0.14em] text-[#C5A059]">
                  {s.status}
                </span>
              </div>
              {s.reply && (
                <div className="mt-3 flex gap-3 border-l border-[#C5A059]/30 pl-3">
                  <p className="whitespace-pre-line text-sm font-light italic leading-relaxed text-[#e8d3a6]">
                    {s.reply}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* --------------------------- Chamber wrapper --------------------------- */
const Chamber = ({ icon: Icon, name, reqTier, memberRank, description, children, delay }) => {
  const unlocked = memberRank >= TIER_RANK[reqTier];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay, ease }}
      className={`relative flex flex-col overflow-hidden rounded-3xl border p-8 ${
        unlocked ? "border-[#C5A059]/20 bg-[#070707]" : "border-white/8 bg-[#050505]"
      }`}
      data-testid={`chamber-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className={`flex items-center gap-3 ${unlocked ? "text-[#C5A059]" : "text-white/35"}`}>
          <Icon size={22} strokeWidth={1.4} />
          <h3 className="font-serif text-2xl font-light">{name}</h3>
        </div>
        <span
          className={`rounded-full border px-3 py-1 text-[0.55rem] uppercase tracking-[0.14em] ${
            unlocked ? "border-[#C5A059]/30 text-[#C5A059]" : "border-white/15 text-white/35"
          }`}
        >
          {reqTier}
        </span>
      </div>
      <p className={`mt-4 text-sm font-light leading-relaxed ${unlocked ? "text-white/60" : "text-white/40"}`}>
        {description}
      </p>

      {unlocked ? (
        children
      ) : (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-dashed border-white/12 px-5 py-4">
          <Lock size={16} className="text-white/40" />
          <p className="text-xs font-light text-white/45">
            Ascend to <span className="text-[#C5A059]">{reqTier}</span> to unseal this chamber.
          </p>
        </div>
      )}
    </motion.div>
  );
};

/* ------------------------------ Section -------------------------------- */
export const Ecosystem = ({ member, onNavigate }) => {
  const rank = TIER_RANK[member?.tier] ?? 0;

  const LinkOut = ({ label, to }) => (
    <button
      onClick={() => onNavigate && onNavigate(to)}
      className="group mt-6 inline-flex items-center gap-2 rounded-full border border-[#C5A059] px-6 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505]"
    >
      {label}
      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );

  return (
    <section id="ecosystem" className="py-28" data-testid="ecosystem-section">
      <p className="overline mb-6">The Ecosystem — Capital &amp; Governance</p>
      <h2 className="mb-6 font-serif text-[clamp(2rem,5vw,4rem)] font-light tracking-tight text-white">
        Pool capital. Move as a <span className="italic text-[#C5A059]">sovereign body</span>.
      </h2>
      <p className="max-w-2xl text-sm font-light leading-relaxed text-white/55">
        Sybils Society operates as a mystery school and governance network, not a trading floor. This is
        the sealed council of <span className="text-[#C5A059]">The Matriarch</span> — where capital is
        pooled and sovereign moves are executed.
      </p>

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Chamber
          icon={Eye}
          name="Governance Scrying Kit"
          reqTier="The Matriarch"
          memberRank={rank}
          description="Before executing joint investments, read market patterns and long-horizon risk through the proprietary oracle and archetype framework."
          delay={0}
        >
          <ScryingChamber />
        </Chamber>

        <Chamber
          icon={Coins}
          name="Capital Pooling"
          reqTier="The Matriarch"
          memberRank={rank}
          description="Within the Inner Sanctum, matriarchs pool resources and align on strategic ventures — the high council where shared capital is committed to collective moves."
          delay={0.06}
        >
          <LinkOut label="Join the collective circle" to="council-circles" />
        </Chamber>

        <Chamber
          icon={CalendarClock}
          name="The Codex of Ruled Time"
          reqTier="The Matriarch"
          memberRank={rank}
          description="Track asset distribution and multi-entity strategy against lunar and energetic cycles rather than linear timelines — deploying capital in rhythm with the heavens."
          delay={0.12}
        >
          <CyclicalPlanner />
        </Chamber>

        <Chamber
          icon={Gem}
          name="Private Oracle Sessions"
          reqTier="The Matriarch"
          memberRank={rank}
          description="Refine high-stakes financial and corporate structures — transitioning or structuring multi-entity operations — through private advisory that clears blocks and secures sovereign wealth transfer."
          delay={0.18}
        >
          <OracleSessionChamber />
        </Chamber>
      </div>

      <p className="mt-10 text-xs font-light italic text-white/35">
        The Ecosystem is the private council of <span className="text-[#C5A059]">The Matriarch</span>.
      </p>
    </section>
  );
};

export default Ecosystem;
